import { useState, useMemo, useEffect } from "react";
import {
  useListEcoleMotifs,
  useListEcoleEleves,
  useGetEcoleEleveDetail,
  useCreateEcoleManualPaiement,
} from "@/lib/backoffice-hooks";
import EcoleShell from "@/components/layout/EcoleShell";
import StepUpReauthDialog from "@/components/StepUpReauthDialog";
import { useCurrentUser } from "@/lib/auth";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { useSearch } from "wouter";
import { CheckCircle2, Download, Eye } from "lucide-react";
import { downloadRecuPdf } from "@/lib/downloadRecu";
import RecuPreviewDialog from "@/components/RecuPreviewDialog";
import AnneeInactiveBanner from "@/components/AnneeInactiveBanner";
import { useAnneeScope } from "@/lib/annee-scope";
import { estParti, type BackofficePaiement } from "@/lib/api";
import { friendlyError } from "@/lib/errors";

const METHODES = [
  "ESPECES_CAISSE", "CHEQUE_CAISSE", "VIREMENT", "CHEQUE",
  "VERSEMENT_BANQUE", "MANDAT", "TPE", "TIERS", "CAGNOTTE", "AUTRE_HORS_LIGNE",
] as const;
const METHODE_LABEL: Record<string, string> = {
  ESPECES_CAISSE: "Espèces (caisse)",
  CHEQUE_CAISSE: "Chèque (caisse)",
  VIREMENT: "Virement bancaire",
  CHEQUE: "Chèque", VERSEMENT_BANQUE: "Versement banque",
  MANDAT: "Mandat", TPE: "TPE", TIERS: "Tiers", CAGNOTTE: "Cagnotte",
  AUTRE_HORS_LIGNE: "Autre",
};

function fcfa(n: number) { return new Intl.NumberFormat("fr-FR").format(n) + " FCFA"; }

export default function SaisiePage() {
  const search = useSearch();
  const params = new URLSearchParams(search);
  const prefilledEleveId = params.get("eleveId") ?? "";
  const prefilledMotifId = params.get("motifId") ?? "";
  const { isActiveAnnee } = useAnneeScope();
  const { user } = useCurrentUser();
  const { data: motifs } = useListEcoleMotifs();
  const { data: roster } = useListEcoleEleves();
  // Élève parti (candidat refusé, départ, transfert sortant) : plus proposé à
  // l'encaissement (21/10/2026). Le registre le garde, avec son statut.
  const eleves = useMemo(() => roster?.filter((e) => !estParti(e)), [roster]);
  // Élève parti désigné par l'adresse (« Encaisser » depuis sa fiche) : la
  // sélection est retirée, et l'écran dit pourquoi.
  const prefillParti = !!prefilledEleveId && !!roster?.some((e) => e.id === prefilledEleveId && estParti(e));
  const mutation = useCreateEcoleManualPaiement();

  const [eleveId, setEleveId] = useState(prefilledEleveId);
  useEffect(() => {
    if (prefillParti) setEleveId("");
  }, [prefillParti]);
  const [motifId, setMotifId] = useState(prefilledMotifId);
  const [montantXof, setMontantXof] = useState<number>(0);
  const [methode, setMethode] = useState<(typeof METHODES)[number]>("ESPECES_CAISSE");
  const [reference, setReference] = useState("");
  const [notes, setNotes] = useState("");
  const [eleveSearch, setEleveSearch] = useState("");
  const [result, setResult] = useState<BackofficePaiement | null>(null);
  const [apercuOpen, setApercuOpen] = useState(false);
  // Step-up : ré-authentification avant d'encaisser un paiement (action sensible).
  const [stepUpOpen, setStepUpOpen] = useState(false);

  // Soldes de L'ÉLÈVE choisi (motifs actifs × tarif de SA classe × déjà payé) :
  // la liste des motifs proposés à l'encaissement en découle — fini le
  // catalogue générique déconnecté de l'élève.
  const { data: eleveDetail, isLoading: soldesLoading } = useGetEcoleEleveDetail(eleveId);
  const soldesPayables = useMemo(
    () => (eleveDetail?.soldes ?? []).filter((s) => s.duXof > 0),
    [eleveDetail],
  );

  useEffect(() => {
    if (prefilledMotifId && eleveDetail) {
      const s = eleveDetail.soldes.find((x) => x.motifId === prefilledMotifId && x.duXof > 0);
      if (s && montantXof === 0) {
        setMotifId(prefilledMotifId);
        // Pré-rempli avec le RESTANT de l'élève pour ce motif.
        setMontantXof(s.restantXof);
      }
    }
  }, [prefilledMotifId, eleveDetail]);

  useEffect(() => {
    if (prefilledEleveId && eleves && !eleveSearch) {
      const e = eleves.find((x) => x.id === prefilledEleveId);
      if (e) setEleveSearch(`${e.prenoms} ${e.nom}`);
    }
  }, [prefilledEleveId, eleves]);

  const selectedMotif = useMemo(() => motifs?.find((m) => m.id === motifId), [motifs, motifId]);
  const selectedSolde = useMemo(
    () => soldesPayables.find((s) => s.motifId === motifId),
    [soldesPayables, motifId],
  );
  const selectedEleve = useMemo(() => eleves?.find((e) => e.id === eleveId), [eleves, eleveId]);
  // Audit SCOM-07 du 21/10/2026 : montant plafonné au reste dû du motif (le
  // serveur refuse au-delà, 409). La monnaie se rend au guichet.
  const depasseReste = !!selectedSolde && montantXof > selectedSolde.restantXof;
  const filteredEleves = useMemo(() => {
    const q = eleveSearch.toLowerCase();
    return (eleves ?? []).filter((e) =>
      !q || `${e.prenoms} ${e.nom} ${e.matriculeInterne} ${e.classe ?? ""}`.toLowerCase().includes(q)
    ).slice(0, 8);
  }, [eleves, eleveSearch]);

  function onMotifChange(id: string) {
    setMotifId(id);
    const s = soldesPayables.find((x) => x.motifId === id);
    // Pré-rempli avec le RESTANT de l'élève pour ce motif (tarif de SA
    // classe moins le déjà-payé), modifiable pour un versement partiel.
    if (s) setMontantXof(s.restantXof);
  }

  function submit() {
    /* v8 ignore start -- garde défensive : le bouton « Enregistrer » est déjà
       désactivé tant que élève/motif/montant sont invalides (mêmes conditions). */
    if (!eleveId || !motifId || montantXof < 100) {
      toast.error("Champs incomplets");
      return;
    }
    /* v8 ignore stop */
    // Encaissement d'un paiement = action sensible → step-up (ré-auth).
    setStepUpOpen(true);
  }

  function runSubmit() {
    mutation.mutate({ data: {
      eleveId, motifId, montantXof, methode,
      reference: reference || null,
      notes: notes || null,
      // SCOM-06 (22/10/2026) : le reçu est émis à l'enregistrement, comme dans
      // School Pro. Avant, le bouton promettait le reçu sans le demander.
      genererRecu: true,
    }}, {
      onSuccess: (p) => {
        toast.success("Paiement enregistré" + (p.reference ? " · " + p.reference : ""));
        // Paiement back-office authentifié → on NE redirige PAS vers la page de
        // suivi PUBLIQUE (/paiement/{token}) : p.id n'est pas un guestToken, donc
        // /api/paiements-public/{id} renvoie 404 « token inconnu » (« Paiement
        // introuvable »). On affiche un état de succès + téléchargement du reçu
        // via l'endpoint authentifié /api/paiements/{id}/recu.pdf.
        setResult(p);
      },
      onError: (err: unknown) => toast.error(friendlyError(err)),
    });
  }

  function resetForm() {
    setResult(null);
    setEleveId("");
    setMotifId("");
    setMontantXof(0);
    setMethode("ESPECES_CAISSE");
    setReference("");
    setNotes("");
    setEleveSearch("");
  }

  if (result) {
    return (
      <EcoleShell>
        <div className="max-w-xl mx-auto mt-8">
          <Card className="p-8 border border-emerald-200 bg-emerald-50 text-center">
            <CheckCircle2 className="w-14 h-14 text-emerald-600 mx-auto mb-3" />
            <h1 className="text-xl font-bold text-navy mb-1">Paiement enregistré</h1>
            {result.reference && (
              <p className="font-mono text-sm text-slate-600 mb-1" style={{ fontFamily: "JetBrains Mono, monospace" }}>
                {result.reference}
              </p>
            )}
            <p className="text-sm text-slate-600 mb-6">
              {fcfa(result.montantXof)} · {METHODE_LABEL[result.methode] ?? result.methode}
            </p>
            <div className="flex flex-col gap-2">
              <Button
                onClick={() => setApercuOpen(true)}
                variant="outline"
                className="border-navy/30 text-navy hover:bg-navy/5"
                data-testid="button-apercu-recu"
              >
                <Eye className="w-4 h-4 mr-1.5" /> Aperçu du reçu
              </Button>
              <Button
                onClick={() => downloadRecuPdf(result.id, result.reference ?? result.id)}
                className="bg-navy text-white hover:bg-navy/90"
                data-testid="button-download-recu"
              >
                <Download className="w-4 h-4 mr-1.5" /> Télécharger le reçu PDF
              </Button>
              <Button variant="outline" onClick={resetForm} data-testid="button-nouvelle-saisie">
                Nouvelle saisie
              </Button>
            </div>
          </Card>
        </div>

        <RecuPreviewDialog
          open={apercuOpen}
          onOpenChange={setApercuOpen}
          source={{ kind: "auth", paiementId: result.id }}
          reference={result.reference ?? result.id}
        />
      </EcoleShell>
    );
  }

  return (
    <EcoleShell>
      <div className="mb-5">
        <h1 className="text-2xl font-bold text-navy">Saisie d'un paiement hors-ligne</h1>
        <p className="text-sm text-slate-600">Espèces, chèque, virement, mandat… Le reçu PDF certifié est généré immédiatement.</p>
      </div>

      {!isActiveAnnee && <AnneeInactiveBanner />}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-4">
          <Card className="p-5 border border-slate-200 shadow-none">
            <h2 className="text-sm font-semibold text-navy mb-3">1. Élève</h2>
            <Input placeholder="Rechercher par nom, matricule, classe…" value={eleveSearch} onChange={(e) => setEleveSearch(e.target.value)} data-testid="input-search-eleve" />
            {prefillParti && (
              <p className="text-xs text-amber-700 mt-2" data-testid="eleve-parti">
                L'élève demandé est parti (sorti ou transféré) : il n'est plus proposé à l'encaissement.
              </p>
            )}
            <div className="mt-3 space-y-1 max-h-56 overflow-auto">
              {filteredEleves.length === 0 && <div className="text-sm text-slate-500 py-2">Aucun élève. Enregistrez-en un d'abord.</div>}
              {filteredEleves.map((e) => (
                <button
                  key={e.id}
                  type="button"
                  onClick={() => {
                    // Changer d'élève invalide le motif/montant en cours : les
                    // soldes sont propres à chaque élève.
                    setEleveId(e.id);
                    setMotifId("");
                    setMontantXof(0);
                  }}
                  className={`w-full text-left px-3 py-2 rounded text-sm border ${eleveId === e.id ? "border-navy bg-navy/5" : "border-slate-200 hover:bg-slate-50"}`}
                  data-testid={`pick-eleve-${e.matriculeInterne}`}
                >
                  <div className="flex justify-between">
                    <span className="font-medium">{e.prenoms} <span className="uppercase">{e.nom}</span></span>
                    <span className="font-mono text-xs text-slate-500" style={{ fontFamily: "JetBrains Mono, monospace" }}>{e.matriculeInterne}</span>
                  </div>
                  <div className="text-xs text-slate-500">{e.classe ?? "Non renseigné"} · {e.anneeScolaire}</div>
                </button>
              ))}
            </div>
          </Card>

          <Card className="p-5 border border-slate-200 shadow-none">
            <h2 className="text-sm font-semibold text-navy mb-3">2. Motif & montant</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label>Motif</Label>
                <select
                  className="w-full border border-slate-300 rounded px-2 h-9 text-sm"
                  value={motifId}
                  onChange={(e) => onMotifChange(e.target.value)}
                  disabled={!eleveId || soldesLoading}
                  data-testid="select-motif"
                >
                  <option value="">
                    {!eleveId
                      ? "Choisissez d'abord un élève"
                      : soldesLoading
                        ? "Chargement des soldes…"
                        : "Choisir"}
                  </option>
                  {soldesPayables.map((s) => (
                    // Un motif soldé reste visible mais ne se choisit plus (SCOM-07).
                    <option key={s.motifId} value={s.motifId} disabled={s.restantXof <= 0}>
                      {s.motifLibelle}
                      {s.restantXof > 0 ? ` · reste ${fcfa(s.restantXof)}` : " · soldé"}
                    </option>
                  ))}
                </select>
                {!!eleveId && !soldesLoading && !!eleveDetail && soldesPayables.length === 0 && (
                  <p className="text-xs text-amber-700 mt-1" data-testid="no-tarif-eleve">
                    Aucun tarif défini pour cet élève ({selectedEleve?.classe ?? "sans classe"}) :
                    fixez les tarifs par niveau dans « Motifs de paiement ».
                  </p>
                )}
              </div>
              <div>
                <Label>Montant à encaisser (FCFA)</Label>
                <Input type="number" value={montantXof} max={selectedSolde?.restantXof} onChange={(e) => setMontantXof(Number(e.target.value))} data-testid="input-montant" />
                {selectedMotif?.paiementPartielAutorise && selectedMotif.montantMinXof && (
                  <div className="text-xs text-slate-500 mt-1">Minimum : {fcfa(selectedMotif.montantMinXof)}</div>
                )}
                {depasseReste && (
                  <div className="text-xs text-red-700 mt-1" data-testid="montant-depasse-reste">
                    Montant supérieur au reste ({fcfa(selectedSolde!.restantXof)}). Rendez la monnaie au-delà.
                  </div>
                )}
              </div>
            </div>
          </Card>

          <Card className="p-5 border border-slate-200 shadow-none">
            <h2 className="text-sm font-semibold text-navy mb-3">3. Mode hors-ligne</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label>Mode</Label>
                <select className="w-full border border-slate-300 rounded px-2 h-9 text-sm" value={methode} onChange={(e) => setMethode(e.target.value as any)} data-testid="select-methode">
                  {METHODES.map((m) => <option key={m} value={m}>{METHODE_LABEL[m]}</option>)}
                </select>
              </div>
              <div>
                <Label>Référence (chèque, mandat, virement…)</Label>
                <Input value={reference} onChange={(e) => setReference(e.target.value)} placeholder="N° chèque, ref virement…" />
              </div>
            </div>
            <div className="mt-3">
              <Label>Notes internes (optionnel)</Label>
              <Input value={notes} onChange={(e) => setNotes(e.target.value)} />
            </div>
          </Card>
        </div>

        <div className="space-y-4">
          <Card className="p-5 border border-slate-200 shadow-none bg-navy text-white">
            <div className="text-xs uppercase tracking-wider text-white/70 mb-2">Récapitulatif</div>
            <div className="text-sm space-y-2">
              <Row label="Élève" value={selectedEleve ? `${selectedEleve.prenoms} ${selectedEleve.nom}` : "·"} />
              <Row label="Motif" value={selectedSolde?.motifLibelle ?? selectedMotif?.libelle ?? "·"} />
              <Row label="Mode" value={METHODE_LABEL[methode]} />
              <Row label="Montant" value={montantXof > 0 ? fcfa(montantXof) : "·"} mono />
            </div>
            <Button
              onClick={submit}
              disabled={!isActiveAnnee || mutation.isPending || !eleveId || !motifId || montantXof < 100 || depasseReste}
              className="w-full mt-4 bg-gold hover:bg-gold/90 text-navy font-bold"
              data-testid="button-submit"
            >
              <CheckCircle2 className="w-4 h-4 mr-1.5" />
              {mutation.isPending ? "Enregistrement…" : "Enregistrer et émettre le reçu"}
            </Button>
          </Card>
        </div>
      </div>

      <StepUpReauthDialog
        open={stepUpOpen}
        onOpenChange={setStepUpOpen}
        email={user?.email}
        onVerified={runSubmit}
        title="Confirmez votre identité"
        description="L'enregistrement d'un paiement nécessite de saisir à nouveau votre mot de passe."
      />
    </EcoleShell>
  );
}

function Row({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="flex justify-between border-b border-white/10 pb-1.5">
      <span className="text-white/70 text-xs uppercase tracking-wider">{label}</span>
      <span className={mono ? "font-mono font-bold" : "font-medium text-right"} style={mono ? { fontFamily: "JetBrains Mono, monospace" } : undefined}>{value}</span>
    </div>
  );
}
